import React, { useState, useEffect } from 'react';
import { 
  UserCheck, 
  Save, 
  Upload, 
  Clock, 
  Image as ImageIcon, 
  Eye, 
  X 
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AboutUsPrincipalForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Welcome Message',
    title: 'From the Principal / Head of School',
    subtitle: '',
    name: '',
    credentials: '',
    role: '',
    opening: '',
    paragraph_one: '',
    paragraph_two: '',
    closing: '',
    theme_line: '',
    principal_photo_id: ''
  });

  const [pendingFields, setPendingFields] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/aboutus/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AboutUsPrincipalForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    if (typeof mediaId === 'string' && (mediaId.startsWith('http://') || mediaId.startsWith('https://') || mediaId.startsWith('blob:') || mediaId.startsWith('data:'))) {
      return mediaId;
    }
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    if (mediaMap[String(mediaId)]) return mediaMap[String(mediaId)];
    return resolveMediaSrc(mediaId, mediaMap) || mediaId;
  };

  const loadData = async () => {
    await Promise.all([loadMediaMap(), loadSection()]);
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let principalSection = null;
      
      const adminResponse = await fetch(
        `${apiBase}/aboutus/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          principalSection = result.data.find(s => s.section_type === 'about-us-principal-shell');
        }
      }
      
      if (!principalSection) {
        const publicResponse = await fetch(
          `${apiBase}/aboutus/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            principalSection = result.data.find(s => s.section_type === 'about-us-principal-shell');
          }
        }
      }
      
      if (!principalSection) {
        throw new Error('Principal section not found');
      }

      const currentSectionId = principalSection.id;
      setSectionId(currentSectionId);

      let published = principalSection.content_published;
      if (typeof published === 'string') {
        try { published = JSON.parse(published); } catch (e) { published = {}; }
      }

      const publishedEn = published?.en || {};
      const initialData = {
        kicker: publishedEn.kicker || 'Welcome Message',
        title: publishedEn.title || 'From the Principal / Head of School',
        subtitle: publishedEn.subtitle || '',
        name: publishedEn.name || '',
        credentials: publishedEn.credentials || '',
        role: publishedEn.role || 'Principal / Head of School',
        opening: publishedEn.opening || '',
        paragraph_one: publishedEn.paragraph_one || '',
        paragraph_two: publishedEn.paragraph_two || '',
        closing: publishedEn.closing || '',
        theme_line: publishedEn.theme_line || '',
        principal_photo_id: publishedEn.principal_photo_id || publishedEn.photo || ''
      };

      const pendingMap = {};

      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${currentSectionId}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        const pendingResult = await pendingResponse.json();
        if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
          const { pending_changes } = pendingResult.data;

          if (Array.isArray(pending_changes)) {
            pending_changes.forEach(change => {
              if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                pendingMap[change.field_id] = true;
                
                // CRITICAL: Display proposed/new value while pending!
                let val = change.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }
                if (val !== undefined && val !== null) {
                  initialData[change.field_id] = val;
                }
              }
            });
          }
        }
      }

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingFields(pendingMap);
    } catch (err) {
      console.error('[AboutUsPrincipalForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              const mediaUrl = response.url || response.file_path || response.data?.url || response.data?.file_path;
              if (mediaId) {
                if (mediaUrl) {
                  setMediaMap(prev => ({
                    ...prev,
                    [mediaId]: mediaUrl,
                    [String(mediaId)]: mediaUrl
                  }));
                }
                handleChange('principal_photo_id', mediaId);
                toast.success('Photo uploaded! Click "Save Photo" below to submit for approval.');
              } else {
                toast.error('No media ID in response');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error(`Upload failed (HTTP ${xhr.status})`);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/aboutus/cms/media/upload?section_type=about-us-principal-shell`);
      xhr.withCredentials = true;
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (savedToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${savedToken}`);
      }
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleSubmitField = async (fieldName, customDisplayName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const displayName = customDisplayName || fieldName.replace(/_/g, ' ');
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: displayName,
            new_value: formData[fieldName],
            old_value: originalData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${displayName} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Principal Message Section...</p>
      </div>
    );
  }

  const photoUrl = getMediaUrl(formData.principal_photo_id);

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <UserCheck size={22} />
          </div>
          <div>
            <h2 className="unified-title">Principal's Address & Profile</h2>
            <p className="unified-subtitle">Manage the Principal's portrait photo, qualifications, and official message to students and parents</p>
          </div>
        </div>
      </div>

      {/* Card 1: Section Kicker / Eyebrow */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker / Eyebrow</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <p className="unified-field-hint" style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 10px 0' }}>
          Small category label displayed right above the main title (e.g. "Welcome Message")
        </p>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="100"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleChange('kicker', e.target.value)}
            disabled={pendingFields.kicker || submitting.kicker}
            placeholder="e.g. Welcome Message"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker', 'Section Kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Card 2: Section Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <p className="unified-field-hint" style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 10px 0' }}>
          Main headline for the welcome message (e.g. "From the Principal / Head of School")
        </p>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="150"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            disabled={pendingFields.title || submitting.title}
            placeholder="e.g. From the Principal / Head of School"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', 'Section Title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Card 3: Section Subtitle */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Subtitle (Optional)</label>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <p className="unified-field-hint" style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 10px 0' }}>
          Optional secondary headline or contextual sub-lead beneath the title
        </p>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="250"
            className="unified-input"
            value={formData.subtitle}
            onChange={(e) => handleChange('subtitle', e.target.value)}
            disabled={pendingFields.subtitle || submitting.subtitle}
            placeholder="e.g. Fostering a community of excellence, character, and lifelong curiosity"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle', 'Section Subtitle')}
            disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} /> {submitting.subtitle ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Card 4: Principal Photo (Top priority with thumbnail preview) */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Principal Portrait Photo</label>
          {pendingFields.principal_photo_id && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap', padding: '8px 0 16px' }}>
          {/* Photo Thumbnail */}
          <div
            style={{
              width: '130px',
              height: '150px',
              borderRadius: '12px',
              background: '#0f172a',
              overflow: 'hidden',
              flexShrink: 0,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #cbd5e1',
              boxShadow: '0 4px 10px rgba(0,0,0,0.06)'
            }}
          >
            {photoUrl ? (
              <img
                src={photoUrl}
                alt="Principal Portrait"
                style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                onClick={() => setLightboxImage({ src: photoUrl, title: 'Principal Portrait' })}
              />
            ) : (
              <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                <ImageIcon size={28} />
                <span style={{ fontSize: '11px', fontWeight: '600' }}>No Photo</span>
              </div>
            )}
            {photoUrl && (
              <button
                type="button"
                onClick={() => setLightboxImage({ src: photoUrl, title: 'Principal Portrait' })}
                style={{
                  position: 'absolute',
                  bottom: '6px',
                  right: '6px',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  border: 'none',
                  padding: '4px 6px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="View Full Photo"
              >
                <Eye size={12} />
              </button>
            )}
          </div>

          {/* Upload Controls */}
          <div style={{ flex: 1, minWidth: '240px' }}>
            <p style={{ margin: '0 0 8px', fontSize: '13.5px', fontWeight: '700', color: '#1e293b' }}>
              Official High-Resolution Portrait
            </p>
            <p style={{ margin: '0 0 16px', fontSize: '12.5px', color: '#64748b', lineHeight: 1.5 }}>
              Recommended: 600×800px (3:4 ratio) • JPG, PNG or WebP • Max 5MB.<br />
              {formData.principal_photo_id ? `Current Photo Media: ${formData.principal_photo_id}` : 'No photo uploaded yet.'}
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="file"
                  id="principal-photo-upload"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageUpload}
                  disabled={imageUploading || pendingFields.principal_photo_id}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                />
                <label
                  htmlFor="principal-photo-upload"
                  className="unified-secondary-btn"
                  style={{
                    cursor: pendingFields.principal_photo_id ? 'not-allowed' : 'pointer',
                    opacity: pendingFields.principal_photo_id ? 0.6 : 1
                  }}
                >
                  <Upload size={14} /> {imageUploading ? `Uploading ${imageProgress}%...` : 'Browse & Upload New'}
                </label>
              </div>

              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('principal_photo_id', 'Principal Portrait Photo')}
                disabled={
                  submitting.principal_photo_id ||
                  pendingFields.principal_photo_id ||
                  formData.principal_photo_id === originalData.principal_photo_id ||
                  !formData.principal_photo_id
                }
              >
                <Save size={14} /> {submitting.principal_photo_id ? 'Saving...' : 'Save Photo'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Card 5: Principal Name */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Principal Full Name</label>
          {pendingFields.name && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="100"
            className="unified-input"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            disabled={pendingFields.name || submitting.name}
            placeholder="e.g. Dr. Amelia Hartono"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('name', 'Principal Full Name')}
            disabled={submitting.name || pendingFields.name || formData.name === originalData.name}
          >
            <Save size={14} /> {submitting.name ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Card 6: Academic Credentials */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Credentials & Qualifications</label>
          {pendingFields.credentials && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="100"
            className="unified-input"
            value={formData.credentials}
            onChange={(e) => handleChange('credentials', e.target.value)}
            disabled={pendingFields.credentials || submitting.credentials}
            placeholder="e.g. M.Ed., Ed.D., B.Sc."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('credentials', 'Principal Credentials')}
            disabled={submitting.credentials || pendingFields.credentials || formData.credentials === originalData.credentials}
          >
            <Save size={14} /> {submitting.credentials ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Card 7: Official Role / Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Official Role & Title</label>
          {pendingFields.role && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="100"
            className="unified-input"
            value={formData.role}
            onChange={(e) => handleChange('role', e.target.value)}
            disabled={pendingFields.role || submitting.role}
            placeholder="e.g. Principal & Head of School"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('role', 'Principal Role Title')}
            disabled={submitting.role || pendingFields.role || formData.role === originalData.role}
          >
            <Save size={14} /> {submitting.role ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Card 8: Opening Salutation */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Opening Salutation</label>
          {pendingFields.opening && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength="400"
            className="unified-textarea"
            rows="2"
            value={formData.opening}
            onChange={(e) => handleChange('opening', e.target.value)}
            disabled={pendingFields.opening || submitting.opening}
            placeholder="e.g. Warm greetings and welcome to Sekolah Permata Harapan..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('opening', 'Principal Opening Salutation')}
            disabled={submitting.opening || pendingFields.opening || formData.opening === originalData.opening}
          >
            <Save size={14} /> {submitting.opening ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Card 9: Paragraph One */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">First Paragraph (Welcome & Core Values)</label>
          {pendingFields.paragraph_one && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength="1000"
            className="unified-textarea"
            rows="4"
            value={formData.paragraph_one}
            onChange={(e) => handleChange('paragraph_one', e.target.value)}
            disabled={pendingFields.paragraph_one || submitting.paragraph_one}
            placeholder="First narrative paragraph detailing our school's ethos, nurturing environment, and holistic growth..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('paragraph_one', 'First Paragraph')}
            disabled={submitting.paragraph_one || pendingFields.paragraph_one || formData.paragraph_one === originalData.paragraph_one}
          >
            <Save size={14} /> {submitting.paragraph_one ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Card 10: Paragraph Two */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Second Paragraph (Future Vision & Community)</label>
          {pendingFields.paragraph_two && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength="1000"
            className="unified-textarea"
            rows="4"
            value={formData.paragraph_two}
            onChange={(e) => handleChange('paragraph_two', e.target.value)}
            disabled={pendingFields.paragraph_two || submitting.paragraph_two}
            placeholder="Second narrative paragraph outlining our global curriculum, character building, and partnership with parents..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('paragraph_two', 'Second Paragraph')}
            disabled={submitting.paragraph_two || pendingFields.paragraph_two || formData.paragraph_two === originalData.paragraph_two}
          >
            <Save size={14} /> {submitting.paragraph_two ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Card 11: Closing Remarks */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Closing Valediction</label>
          {pendingFields.closing && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="100"
            className="unified-input"
            value={formData.closing}
            onChange={(e) => handleChange('closing', e.target.value)}
            disabled={pendingFields.closing || submitting.closing}
            placeholder="e.g. In faith, integrity, and lifelong learning,"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('closing', 'Principal Closing')}
            disabled={submitting.closing || pendingFields.closing || formData.closing === originalData.closing}
          >
            <Save size={14} /> {submitting.closing ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Card 12: Theme Line */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Theme Line / Signature Quote</label>
          {pendingFields.theme_line && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="200"
            className="unified-input"
            value={formData.theme_line}
            onChange={(e) => handleChange('theme_line', e.target.value)}
            disabled={pendingFields.theme_line || submitting.theme_line}
            placeholder="e.g. Nurturing Character, Inspiring Excellence, Building Futures."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('theme_line', 'Principal Theme Line')}
            disabled={submitting.theme_line || pendingFields.theme_line || formData.theme_line === originalData.theme_line}
          >
            <Save size={14} /> {submitting.theme_line ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                cursor: 'pointer'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title || 'Principal photo'}
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
